/**
 * User preferences types for Idle Depths.
 *
 * Defines the structure of user preferences stored in localStorage
 * (for all users) and synced to the server (for authenticated users).
 */

/**
 * Theme options for the application.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * User preferences structure.
 *
 * All preferences have sensible defaults defined in DEFAULT_PREFERENCES.
 */
export interface UserPreferences {
  // =========================================================================
  // Audio Settings
  // =========================================================================

  /** Music volume (0-100) */
  musicVolume: number;

  /** Sound effects volume (0-100) */
  soundEffectsVolume: number;

  /** Whether music is enabled */
  musicEnabled: boolean;

  /** Whether sound effects are enabled */
  soundEnabled: boolean;

  // =========================================================================
  // UI Settings
  // =========================================================================

  /** Color theme preference */
  theme: ThemePreference;

  /** Whether to show the guest banner prompting login */
  showGuestBanner: boolean;

  // =========================================================================
  // GDPR Consent
  // =========================================================================

  /** Whether user consented to analytics tracking */
  analyticsConsent: boolean;

  /** Whether user consented to marketing communications */
  marketingConsent: boolean;
}

/**
 * Default preferences for new users/guests.
 *
 * These values are used when no preferences exist in localStorage
 * or when creating a new user account.
 */
export const DEFAULT_PREFERENCES: UserPreferences = {
  // Audio: enabled by default at reasonable levels
  musicVolume: 80,
  soundEffectsVolume: 100,
  musicEnabled: true,
  soundEnabled: true,

  // UI: system theme, show guest banner
  theme: 'system',
  showGuestBanner: true,

  // GDPR: opt-out by default (must explicitly consent)
  analyticsConsent: false,
  marketingConsent: false,
};

/**
 * Validates that a preferences object has all required fields.
 *
 * Used when loading preferences from localStorage to handle
 * schema migrations when new preferences are added.
 *
 * @param preferences - Partial preferences object to validate
 * @returns Complete preferences with defaults for missing fields
 */
export function validatePreferences(preferences: Partial<UserPreferences>): UserPreferences {
  return {
    ...DEFAULT_PREFERENCES,
    ...preferences,
  };
}

/**
 * Type guard to check if an object is a valid UserPreferences.
 *
 * @param value - Unknown value to check
 * @returns True if value is a valid UserPreferences object
 */
export function isValidPreferences(value: unknown): value is Partial<UserPreferences> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const preferences = value as Record<string, unknown>;

  // Check numeric fields
  if (
    preferences.musicVolume !== undefined &&
    (typeof preferences.musicVolume !== 'number' ||
      preferences.musicVolume < 0 ||
      preferences.musicVolume > 100)
  ) {
    return false;
  }

  if (
    preferences.soundEffectsVolume !== undefined &&
    (typeof preferences.soundEffectsVolume !== 'number' ||
      preferences.soundEffectsVolume < 0 ||
      preferences.soundEffectsVolume > 100)
  ) {
    return false;
  }

  // Check boolean fields
  const booleanFields = [
    'musicEnabled',
    'soundEnabled',
    'showGuestBanner',
    'analyticsConsent',
    'marketingConsent',
  ];
  for (const field of booleanFields) {
    if (preferences[field] !== undefined && typeof preferences[field] !== 'boolean') {
      return false;
    }
  }

  // Check theme field
  if (
    preferences.theme !== undefined &&
    !['light', 'dark', 'system'].includes(preferences.theme as string)
  ) {
    return false;
  }

  return true;
}
